// Every value `workspaces_subscription_tier_check` admits in use. `'pro'` stays
// in the constraint, because removing it buys nothing, but no row holds it.
export type SubscriptionTier = 'free' | 'launch' | 'grow' | 'scale' | 'enterprise';

export type WorkspaceFeature =
  | 'quarterly_cycles'      // year/quarter scoping on rocks, metrics, meetings
  | 'scorecard'             // scorecard view, metrics library
  | 'org_chart'             // org chart page
  | 'org_chart_board'       // board band on the org chart
  | 'vision_planning'       // vision, planning, ideas, archive pages
  | 'follow_watch'          // follow/watch buttons on rocks and metrics
  | 'notifications'         // in-app notification creation
  | 'full_role_hierarchy'   // owner/admin/member/observer roles
  | 'audit_log'             // audit log page
  | 'connected_apps'
  | 'data_reporting'        // data reporting section in settings
  | 'scorecard_colors'      // scorecard color thresholds in settings
  | 'meeting_prep_schedule' // scheduled meeting prep emails
  | 'meeting_schedules'     // scheduling a built-in meeting type: recurrence, host, invitees
  | 'meeting_types'         // editing a built-in type's agenda, and creating new types
  | 'meeting_recap'         // generate and send meeting recap
  | 'priority_issues'       // issue priority field and sort
  | 'discussed_section'     // "Discussed" bucket in Actions view
  | 'terminology'           // custom term overrides
  | 'presets'
  | 'stakeholder_matrices'  // RACI-style stakeholder grids on rocks
  | 'setup_wizard'          // onboarding setup wizard
  | 'integrations'          // integrations page
  | 'people_insights'       // people strengths/insights page
  | 'checkin_synthesis'     // AI consolidation of quarterly prep check-ins
  | 'health'                // health diagnostic surface
  | 'teams'                // sub-units within a tenant (TEAMS.md § 6)
  | 'search';               // global search palette

/** Rank, not identity. */
export const TIER_ORDER: Record<SubscriptionTier, number> = {
  free: 0,
  launch: 1,
  grow: 2,
  scale: 3,
  enterprise: 4,
};

/**
 * The lowest tier that may use each feature. A feature absent from a tier is
 * absent because the tier ranks below its minimum — there is no separate list
 * of exclusions by plan.
 */
export const MINIMUM_TIER: Record<WorkspaceFeature, SubscriptionTier> = {
  quarterly_cycles: 'free',
  scorecard: 'free',
  org_chart: 'free',
  vision_planning: 'free',
  notifications: 'free',
  data_reporting: 'free',
  setup_wizard: 'free',
  teams: 'free',
  search: 'free',

  follow_watch: 'launch',
  org_chart_board: 'launch',
  full_role_hierarchy: 'launch',
  scorecard_colors: 'launch',
  discussed_section: 'launch',
  meeting_prep_schedule: 'launch',
  meeting_schedules: 'launch',
  meeting_recap: 'launch',
  integrations: 'launch',
  presets: 'launch',

  meeting_types: 'grow',
  priority_issues: 'grow',
  terminology: 'grow',
  stakeholder_matrices: 'grow',
  people_insights: 'grow',
  // A submission pool of one has nothing to synthesize, so this is excluded
  // from a workspace of one by shape as well as by tier (#377 §5).
  checkin_synthesis: 'grow',
  health: 'grow',

  audit_log: 'scale',
  connected_apps: 'scale',
};

/** Seats and teams a tier may hold. `null` is uncapped. Teams include the root. */
export const TIER_CAPS: Record<SubscriptionTier, { seats: number | null; teams: number | null }> = {
  free:       { seats: 1,    teams: 1 },
  launch:     { seats: 10,   teams: 2 },
  grow:       { seats: 30,   teams: 8 },
  scale:      { seats: 75,   teams: null },
  enterprise: { seats: null, teams: null },
};

/**
 * Features a workspace of one cannot use, because their subject is other
 * people. Shape, not plan: these stay excluded whatever the workspace pays.
 *
 * Everything else a personal workspace lacks it lacks by tier — it is free, so
 * `MINIMUM_TIER` already applies. The difference is what the absence looks
 * like: in a personal workspace a missing feature is absent, not locked behind
 * an upgrade prompt, because its owner reached it by having an identity rather
 * than by choosing a plan.
 */
const PERSONAL_EXCLUDED_FEATURES: WorkspaceFeature[] = [
  'org_chart',
  'follow_watch',
  'stakeholder_matrices',
  'checkin_synthesis',
];

/**
 * What an absent tier is read as. A caller that cannot see the subscription is
 * answered as the entry paid rung rather than as free, so a missing read never
 * silently withdraws a feature someone is paying for.
 */
const ENTRY_PAID_RUNG: SubscriptionTier = 'launch';

export function tierAtLeast(
  tier: SubscriptionTier | undefined | null,
  minimum: SubscriptionTier,
): boolean {
  return TIER_ORDER[tier ?? ENTRY_PAID_RUNG] >= TIER_ORDER[minimum];
}

export function workspaceHasFeature(
  feature: WorkspaceFeature,
  tier: SubscriptionTier | undefined | null = ENTRY_PAID_RUNG,
  isPersonal = false,
): boolean {
  if (isPersonal && PERSONAL_EXCLUDED_FEATURES.includes(feature)) return false;
  return tierAtLeast(tier, MINIMUM_TIER[feature]);
}

/** The cheapest tier that admits `feature`, for resolving an upsell target (#347). */
export function minimumTierFor(feature: WorkspaceFeature): SubscriptionTier {
  return MINIMUM_TIER[feature];
}

export function seatLimitFor(tier: SubscriptionTier | undefined | null): number | null {
  return TIER_CAPS[tier ?? ENTRY_PAID_RUNG].seats;
}

export function teamLimitFor(tier: SubscriptionTier | undefined | null): number | null {
  return TIER_CAPS[tier ?? ENTRY_PAID_RUNG].teams;
}
